import { useState } from "react";
import { ArrowUpRight, Instagram, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Chapter, FadeUp } from "@/components/Reveal";
import { SITE } from "@/data/site";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const CHANNELS = [
    {
      icon: MessageCircle,
      label: t("contact.corporateLabel"),
      value: SITE.whatsappCorporateDisplay,
      href: `https://wa.me/${SITE.whatsappCorporate}`,
      testid: "contact-whatsapp-corporate-button",
    },
    {
      icon: MessageCircle,
      label: t("contact.activationLabel"),
      value: SITE.whatsappActivationDisplay,
      href: `https://wa.me/${SITE.whatsappActivation}`,
      testid: "contact-whatsapp-activation-button",
    },
    {
      icon: Instagram,
      label: "Instagram",
      value: SITE.instagram,
      href: SITE.instagramUrl,
      testid: "contact-instagram-button",
    },
    {
      icon: Mail,
      label: "Email",
      value: SITE.email,
      href: `mailto:${SITE.email}`,
      testid: "contact-email-button",
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`${t("contact.emailSubject")} — ${form.name || "Website visitor"}`);
    const body = encodeURIComponent(`${form.message}\n\n— ${form.name}\n${form.email}`);
    window.location.href = `mailto:${SITE.email}?subject=${subject}&body=${body}`;
    toast.success(t("contact.toast"));
  };

  const field =
    "w-full border-b border-white/15 bg-transparent py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#F97316]";

  return (
    <section id="contact" className="py-28 md:py-40" data-testid="contact-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Chapter number="11" title={t("chapters.contact")} />
        <FadeUp>
          <h2 className="max-w-4xl text-balance font-syne text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl lg:text-[4rem]" data-testid="contact-heading">
            {t("contact.headingA")} <span className="text-[#F97316]">{t("contact.headingB")}</span>
          </h2>
          <p className="mb-14 mt-6 max-w-xl text-sm leading-relaxed text-white/55 md:mb-20 md:text-base">
            {t("contact.sub")}
          </p>
        </FadeUp>

        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          <FadeUp className="lg:col-span-6">
            <form onSubmit={handleSubmit} className="flex flex-col gap-2" data-testid="contact-form">
              <label className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">{t("contact.nameLabel")}</label>
              <input
                type="text"
                required
                placeholder={t("contact.namePh")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
                data-testid="contact-form-name-input"
              />
              <label className="mt-8 font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">{t("contact.emailLabel")}</label>
              <input
                type="email"
                required
                placeholder={t("contact.emailPh")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
                data-testid="contact-form-email-input"
              />
              <label className="mt-8 font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">{t("contact.messageLabel")}</label>
              <textarea
                required
                rows={4}
                placeholder={t("contact.messagePh")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${field} resize-none`}
                data-testid="contact-form-message-input"
              />
              <button
                type="submit"
                className="group mt-12 flex w-fit items-center gap-3 rounded-full bg-[#F97316] px-9 py-4 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:bg-[#EA580C] active:scale-[0.96]"
                data-testid="contact-form-submit-button"
              >
                {t("contact.submit")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <p className="mt-4 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/30">
                {t("contact.note")}
              </p>
            </form>
          </FadeUp>

          <div className="flex flex-col gap-4 lg:col-span-6">
            {CHANNELS.map((c, i) => (
              <FadeUp key={c.label} delay={i * 0.08}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between rounded-2xl border border-white/10 bg-[#111111] p-6 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-[#F97316]/50 md:p-7"
                  data-testid={c.testid}
                >
                  <div className="flex items-center gap-5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 transition-colors duration-500 group-hover:border-[#F97316] group-hover:bg-[#F97316]">
                      <c.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/40">{c.label}</p>
                      <p className="mt-1 font-syne text-base font-bold tracking-tight md:text-lg">{c.value}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-white/30 transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#F97316]" />
                </a>
              </FadeUp>
            ))}
            <FadeUp delay={0.24} className="overflow-hidden rounded-2xl border border-white/10">
              <iframe
                title="Payung Oren Creative — Bekasi Utara area map"
                src="https://maps.google.com/maps?q=Bekasi%20Utara%2C%20Bekasi%2C%20West%20Java%2C%20Indonesia&t=&z=12&ie=UTF8&iwloc=&output=embed"
                className="h-64 w-full border-0 grayscale invert-[0.92] md:h-72"
                loading="lazy"
                data-testid="contact-map"
              />
            </FadeUp>
            <div>
              <p className="font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/40">
                {t("contact.mapCaption")}
              </p>
              <p className="mt-2 font-spacemono text-[10px] uppercase tracking-[0.25em] text-[#F97316]/60">
                {t("contact.areas")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
